import { useEffect, useState, useCallback } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { adminNav } from "@/config/nav";
import api, { formatINR, API_BASE } from "@/lib/api";
import { Spinner } from "@/components/Loaders";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Bank, TrendUp, ArrowClockwise, DownloadSimple, MagnifyingGlass, Percent } from "@phosphor-icons/react";

const RANGES = [
  { k: "today", l: "Today" }, { k: "yesterday", l: "Yesterday" }, { k: "week", l: "This Week" },
  { k: "month", l: "This Month" }, { k: "all", l: "All Time" }, { k: "custom", l: "Custom" },
];
const CATS = ["", "Farmhouses", "Resorts", "Banquet Halls", "Function Halls"];

const Card = ({ label, value, accent, testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-stone-200 bg-white p-4">
    <p className="text-xs text-stone-400">{label}</p>
    <p className={`mt-1 font-heading text-xl font-bold ${accent || "text-stone-900"}`}>{value}</p>
  </div>
);

export default function AdminFinance() {
  const [range, setRange] = useState("month");
  const [category, setCategory] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comm, setComm] = useState(null);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [integ, setInteg] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = { range, category };
    if (range === "custom" && start && end) { params.start = start; params.end = end; }
    api.get("/admin/finance/dashboard", { params }).then((r) => setD(r.data)).catch(() => toast.error("Couldn't load finance data")).finally(() => setLoading(false));
    api.get("/admin/finance/commission-report", { params }).then((r) => setRows(r.data.rows)).catch(() => {});
  }, [range, category, start, end]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/commission-config").then((r) => setComm(r.data)).catch(() => {}); api.get("/admin/integration-status").then((r) => setInteg(r.data)).catch(() => {}); }, []);

  const saveCommission = () => {
    const s = {
      commission_rate: parseFloat(comm.default) || 0,
      commission_farmhouses: comm.categories.Farmhouses,
      commission_resorts: comm.categories.Resorts,
      commission_banquet_halls: comm.categories["Banquet Halls"],
      commission_function_halls: comm.categories["Function Halls"],
    };
    api.put("/admin/settings", { settings: s }).then(() => { toast.success("Commission settings saved"); load(); }).catch(() => toast.error("Save failed"));
  };

  const doExport = (type) => {
    const token = localStorage.getItem("avb_token");
    fetch(`${API_BASE}/admin/finance/export?type=${type}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a"); a.href = url; a.download = `avinya_${type}.csv`; a.click();
        URL.revokeObjectURL(url);
      }).catch(() => toast.error("Export failed"));
  };

  const search = () => {
    if (!q.trim()) return;
    api.get("/admin/finance/search", { params: { q } }).then((r) => setResults(r.data.results)).catch(() => {});
  };

  return (
    <DashboardLayout nav={adminNav} title="Admin">
      <div className="flex items-center gap-2">
        <Bank size={26} className="text-primary" />
        <h1 className="font-heading text-2xl font-semibold text-stone-900 md:text-3xl">Finance</h1>
        <button data-testid="finance-refresh" onClick={load} className="ml-auto rounded-lg border border-stone-200 p-2 hover:bg-accent"><ArrowClockwise size={18} /></button>
      </div>
      <p className="mt-1 text-sm text-stone-500">All figures computed on the backend from real transactions. Currency: INR.</p>

      {/* Filters */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {RANGES.map((r) => (
          <button key={r.k} data-testid={`finance-range-${r.k}`} onClick={() => setRange(r.k)}
            className={`rounded-full px-3 py-1.5 text-sm font-medium ${range === r.k ? "bg-primary text-white" : "border border-stone-200 text-stone-600 hover:bg-accent"}`}>{r.l}</button>
        ))}
        <select data-testid="finance-category" value={category} onChange={(e) => setCategory(e.target.value)} className="rounded-full border border-stone-200 px-3 py-1.5 text-sm text-stone-600">
          {CATS.map((c) => <option key={c} value={c}>{c || "All categories"}</option>)}
        </select>
        {range === "custom" && (
          <>
            <Input type="date" data-testid="finance-start" value={start} onChange={(e) => setStart(e.target.value)} className="h-9 w-auto" />
            <Input type="date" data-testid="finance-end" value={end} onChange={(e) => setEnd(e.target.value)} className="h-9 w-auto" />
          </>
        )}
      </div>

      {loading || !d ? <div className="flex justify-center py-16"><Spinner /></div> : (
        <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="finance-cards">
          <Card label="Total Booking Value (GMV)" value={formatINR(d.gmv)} testid="fc-gmv" />
          <Card label="Customer Payments" value={formatINR(d.customer_payments)} testid="fc-payments" />
          <Card label="Avinya Commission" value={formatINR(d.commission)} accent="text-primary" testid="fc-commission" />
          <Card label="Owner Earnings" value={formatINR(d.owner_earnings)} testid="fc-owner" />
          <Card label="Refunds" value={formatINR(d.refunds)} accent="text-red-600" testid="fc-refunds" />
          <Card label="Pending Owner Payouts" value={formatINR(d.pending_payouts)} accent="text-amber-600" testid="fc-pending-payouts" />
          <Card label="Completed Owner Payouts" value={formatINR(d.completed_payouts)} accent="text-green-600" testid="fc-completed-payouts" />
          <Card label="Outstanding Balance" value={formatINR(d.outstanding_balance)} testid="fc-outstanding" />
          <Card label="Failed Payments" value={d.failed_payments} testid="fc-failed-payments" />
          <Card label="Failed Payouts" value={d.failed_payouts} testid="fc-failed-payouts" />
          <Card label="Net Avinya Revenue" value={formatINR(d.net_avinya_revenue)} accent="text-primary" testid="fc-net-revenue" />
          <Card label="Paid Bookings" value={d.paid_bookings} testid="fc-paid-bookings" />
        </div>
      )}

      {/* Exports */}
      <div className="mt-5 flex flex-wrap gap-2">
        {["bookings", "payments", "payouts", "commission", "refunds"].map((t) => (
          <Button key={t} data-testid={`finance-export-${t}`} variant="outline" size="sm" className="gap-1.5 rounded-full" onClick={() => doExport(t)}>
            <DownloadSimple size={16} /> {t.charAt(0).toUpperCase() + t.slice(1)} CSV
          </Button>
        ))}
      </div>

      {/* Integration status */}
      {integ && (
        <div className="mt-8 rounded-2xl border border-stone-200 bg-white p-5" data-testid="integration-status">
          <p className="font-heading font-semibold text-stone-900">Integration Status</p>
          <p className="mt-1 text-xs text-stone-400">Live money & messaging activate when keys are added to the backend environment.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {[
              ["Razorpay Payments", integ.razorpay, integ.razorpay ? integ.razorpay_mode : "add keys"],
              ["Razorpay Webhook", integ.razorpay_webhook, ""],
              ["RazorpayX Payouts", integ.razorpayx, integ.razorpayx ? "" : "manual mode"],
              ["WhatsApp (Twilio)", integ.whatsapp, integ.whatsapp ? "" : "click-to-chat"],
              ["Email (Resend)", integ.email, ""],
              ["Google Maps", integ.maps, ""],
            ].map(([label, ok, note]) => (
              <span key={label} data-testid={`integ-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`rounded-full px-3 py-1.5 text-xs font-medium ${ok ? "bg-green-100 text-green-700" : "bg-stone-100 text-stone-500"}`}>
                {ok ? "● " : "○ "}{label}{note ? ` · ${note}` : ""}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Commission engine */}
      {comm && (
        <div className="mt-8 rounded-2xl border border-stone-200 bg-white p-5">
          <p className="flex items-center gap-2 font-heading font-semibold text-stone-900"><Percent size={18} className="text-primary" /> Commission Engine</p>
          <p className="mt-1 text-xs text-stone-400">Priority: property-specific → category → default. Confirmed bookings keep their locked rate.</p>
          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-5">
            <div><label className="text-xs text-stone-500">Default %</label><Input data-testid="comm-default" type="number" value={comm.default} onChange={(e) => setComm({ ...comm, default: e.target.value })} /></div>
            {Object.keys(comm.categories).map((c) => (
              <div key={c}><label className="text-xs text-stone-500">{c} %</label>
                <Input data-testid={`comm-${c.replace(/\s+/g, "-").toLowerCase()}`} type="number" placeholder="default" value={comm.categories[c]}
                  onChange={(e) => setComm({ ...comm, categories: { ...comm.categories, [c]: e.target.value } })} /></div>
            ))}
          </div>
          <Button data-testid="comm-save" className="mt-4 rounded-full" onClick={saveCommission}>Save commission settings</Button>
        </div>
      )}

      {/* Commission report */}
      <div className="mt-8">
        <p className="flex items-center gap-2 font-heading font-semibold text-stone-900"><TrendUp size={18} className="text-primary" /> Commission Report</p>
        <div className="mt-3 overflow-x-auto rounded-2xl border border-stone-200 bg-white">
          <table className="w-full text-sm" data-testid="commission-report-table">
            <thead className="bg-stone-50 text-left text-xs text-stone-500"><tr>
              <th className="p-3">Booking</th><th className="p-3">Property</th><th className="p-3">Category</th>
              <th className="p-3 text-right">Amount</th><th className="p-3 text-right">Rate</th><th className="p-3 text-right">Commission</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={6} className="p-6 text-center text-stone-400">No paid bookings in this period.</td></tr> :
                rows.map((r) => (
                  <tr key={r.booking_ref} className="border-t border-stone-100">
                    <td className="p-3 font-medium text-stone-800">{r.booking_ref}</td>
                    <td className="p-3 text-stone-600">{r.property}</td>
                    <td className="p-3 text-stone-500">{r.category}</td>
                    <td className="p-3 text-right">{formatINR(r.booking_amount)}</td>
                    <td className="p-3 text-right">{r.commission_rate}%</td>
                    <td className="p-3 text-right font-semibold text-primary">{formatINR(r.final_commission)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Financial search */}
      <div className="mt-8">
        <p className="flex items-center gap-2 font-heading font-semibold text-stone-900"><MagnifyingGlass size={18} className="text-primary" /> Financial Search</p>
        <div className="mt-3 flex gap-2">
          <Input data-testid="finance-search-input" placeholder="Booking ID, payment ID, customer or property" value={q}
            onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} />
          <Button data-testid="finance-search-btn" onClick={search} className="rounded-full">Search</Button>
        </div>
        {results && (
          <div className="mt-3 space-y-2" data-testid="finance-search-results">
            {results.length === 0 ? <p className="text-sm text-stone-400">No matches.</p> :
              results.map((r) => (
                <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-stone-200 bg-white px-4 py-3 text-sm">
                  <div><span className="font-semibold text-stone-800">{r.booking_ref}</span> · {r.property_name} · {r.customer_name} · {r.date}</div>
                  <div className="text-stone-500">Total {formatINR(r.customer_total)} · Commission {formatINR(r.avinya_revenue)} · Owner {formatINR(r.owner_earnings)} · <span className="uppercase">{r.payment_status}</span></div>
                </div>
              ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
